import { useState, useEffect } from 'react'
import { useUIStore } from '@/stores/ui.store'
import { useTemplates, useDeleteTemplate } from '@/hooks/useNotificacoes'
import {
    Plus, Search, Loader2, MessageSquare, Edit, Trash2,
    ChevronLeft, Palette, Smartphone, Globe, Mail
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { TemplateForm } from '@/components/admin/TemplateForm'
import type { NotificacaoTemplate, UUID } from '@/types/database'

export function TemplatesPage() {
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    const [search, setSearch] = useState('')
    const [showForm, setShowForm] = useState(false)
    const [editing, setEditing] = useState<NotificacaoTemplate | null>(null)

    useEffect(() => { setPageTitle('Templates de Mensagem') }, [setPageTitle])

    const { data: templates, isLoading } = useTemplates()
    const deleteMut = useDeleteTemplate()

    const filtered = templates?.filter(t =>
        t.nome.toLowerCase().includes(search.toLowerCase()) ||
        t.conteudo.toLowerCase().includes(search.toLowerCase())
    ) || []

    const handleDelete = (id: string) => {
        if (confirm('Tem certeza que deseja excluir este template?')) {
            deleteMut.mutate(id)
        }
    }

    const handleEdit = (template: NotificacaoTemplate) => {
        setEditing(template)
        setShowForm(true)
    }

    const handleCreate = () => {
        setEditing(null)
        setShowForm(true)
    }

    const getIcon = (canal: string) => {
        switch (canal) {
            case 'whatsapp': return <Smartphone size={18} />
            case 'web_push': return <Globe size={18} />
            case 'email': return <Mail size={18} />
            default: return <MessageSquare size={18} />
        }
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Link to="/admin/notificacoes" className="p-2 rounded-lg hover:bg-surface-100 text-surface-600 transition-colors">
                        <ChevronLeft size={20} />
                    </Link>
                    <div>
                        <h2 className="text-xl font-bold text-surface-900">Templates</h2>
                        <p className="text-sm text-surface-700/60 mt-0.5">Modelos de mensagens pré-configurados</p>
                    </div>
                </div>
                <button onClick={handleCreate} className="btn btn-primary gap-2">
                    <Plus size={18} /> Novo Template
                </button>
            </div>

            {/* Toolbar */}
            <div className="glass-card p-4 flex gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-700/40" />
                    <input
                        type="text" value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Buscar por nome ou conteúdo..."
                        className="input pl-10"
                    />
                </div>
            </div>

            {/* Grid */}
            {isLoading ? (
                <div className="flex justify-center p-12">
                    <Loader2 size={24} className="animate-spin text-primary-600" />
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-16 text-surface-700/60">
                    <Palette size={40} className="mx-auto mb-3 opacity-20" />
                    <p>Nenhum template encontrado</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filtered.map(template => (
                        <div key={template.id} className="glass-card flex flex-col group hover:shadow-xl hover:shadow-primary-500/5 transition-all duration-300">
                            <div className="p-5 flex-1">
                                <div className="flex items-start justify-between mb-4">
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center
                                        ${template.canal === 'whatsapp' ? 'bg-green-100 text-green-600' :
                                            template.canal === 'web_push' ? 'bg-primary-100 text-primary-600' :
                                                'bg-blue-100 text-blue-600'}`}>
                                        {getIcon(template.canal)}
                                    </div>
                                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border
                                        ${template.ativo ? 'bg-green-50 text-green-600 border-green-100' : 'bg-surface-100 text-surface-400 border-surface-200'}`}>
                                        {template.ativo ? 'Ativo' : 'Inativo'}
                                    </span>
                                </div>
                                <h3 className="font-bold text-surface-900 group-hover:text-primary-600 transition-colors">{template.nome}</h3>
                                <p className="text-sm text-surface-700/60 mt-1 line-clamp-3 font-mono bg-surface-50/50 p-2 rounded-lg border border-surface-100">
                                    {template.conteudo}
                                </p>
                            </div>
                            <div className="p-3 border-t border-surface-100 bg-surface-50/50 flex items-center justify-between">
                                <div className="flex items-center gap-1">
                                    <span className="text-[10px] uppercase font-bold text-surface-700/40 px-2">
                                        {template.canal}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => handleEdit(template)}
                                        className="p-2 rounded-lg hover:bg-white hover:shadow-sm text-surface-700/60 hover:text-primary-600 transition-all">
                                        <Edit size={16} />
                                    </button>
                                    <button onClick={() => handleDelete(template.id)}
                                        className="p-2 rounded-lg hover:bg-danger-50 text-surface-700/60 hover:text-danger-600 transition-all">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal Form */}
            {showForm && (
                <TemplateForm
                    template={editing}
                    onClose={() => setShowForm(false)}
                />
            )}
        </div>
    )
}
